import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, intlShape, injectIntl } from 'react-intl';
import { default as timeZoneIds } from '../../../i18n/timeZones';
import moment from 'moment';
import * as Util from '../../../common/utilities';
import { getBase64 } from '../../../utilities/objectUtils';
import {
  Button,
  Combobox,
  Datepicker,
  Icon,
  Input,
  Radio,
  Spinner,
  Timepicker,
  Tooltip
} from '@salesforce/design-system-react';

import { AssetPicker, ContentBuilderModal, ImageURLModal } from '../index';

import './AssetEditFields.scss';

class AssetEditFields extends Component {
  constructor(props) {
    super(props);
    this.state = {
      type: {
        inputValue: ''
      },
      showUpload: !(
        this.props.modals?.assetWizard.data?.asset?.image_link ||
        this.props.modals?.assetWizard.data?.asset?.image_url ||
        this.props.modals?.assetWizard.data?.asset?.contentBuilderAsset?.content
      ),
      contentBuilderPreviewAsset: undefined,
      imageLoadError: undefined,
      dragging: false,
      url: undefined,
      urlSubmit: false
    };
    this.fields = [];
    this.inputFileRef = React.createRef();
  }

  /**
   * Creates an Input control for the given field metadata
   *
   * @param data
   *   value: current string value
   *   field: internal name of field in redux
   *   custom: true if the field is a custom field, false otherwise
   *   label: when custom === false, id to use for translation of the field label
   *          when custom === true, the untranslatable label for the custom field
   * @returns <Input /> component
   */
  createInput(data) {
    const { formatMessage } = this.props.intl;
    const label = data.custom ? data.label : formatMessage({ id: data.label });
    const tooltipText = !!data.tooltip ? (
      <Tooltip
        id={`${data.field}-tooltip`}
        align="right"
        content={formatMessage({
          id: data.tooltip
        })}
      />
    ) : null;

    const onBlur = () => {
      if (data.onBlur) {
        data.onBlur(data, this.fields);
      }

      if (this.props.onFieldBlur) {
        this.props.onFieldBlur(data, this.fields);
      }
    };

    if (data.field === 'image_url') {
      return this.createImage(data);
    }

    return (
      <Input
        key={`${data.field}-input`}
        className="field-input"
        value={data.value || ''}
        required={data.required}
        disabled={data.disabled}
        errorText={data.errorText}
        label={label}
        placeholder={data.placeholder}
        onBlur={onBlur}
        onChange={(_evt, { value }) =>
          this.props.onFieldChange(data, value, this.fields)
        }
        fieldLevelHelpTooltip={tooltipText}
      />
    );
  }

  uploadImagePreview(data) {
    const { formatMessage } = this.props.intl;
    const wizardData = this.props.modals?.assetWizard?.data;
    const asset = wizardData?.asset;
    const imageLabel = formatMessage({
      id: 'components.ecs.assetWizard.imageLabel'
    });
    const hasError =
      wizardData?.mode === 'create'
        ? wizardData.errors?.image_url?.length > 0
        : !(asset?.image_link || asset.image_url);

    let deleteButton = (
      <Button
        assistiveText={{ label: 'Delete' }}
        iconCategory="utility"
        iconName="close"
        variant="icon"
        inverse
        size="xx-small"
        className="delete-image-button"
        onClick={() => this.resetImage()}
      />
    );

    let imagePreview = undefined;

    // HTML blocks need to generate a preview from the server
    if (asset.content_builder_type === 'html' && !this.state.urlSubmit) {
      const previewId = asset.contentBuilderAsset.id;
      const preview = this.props.contentBuilder.data?.previews?.[previewId];
      if (preview?.error) {
        imagePreview = undefined;
      } else if (preview?.isLoading) {
        imagePreview = (
          <div className="preview-spinner">
            <Spinner variant="brand" />
          </div>
        );
        deleteButton = undefined;
      } else if (asset?.image_link && !this.state.urlSubmit) {
        imagePreview = <img src={asset?.image_link} alt="Asset preview" />;
      } else {
        if (preview?.dataUri) {
          imagePreview = <img src={preview.dataUri} alt="Asset preview" />;
        }
      }
    } else if (asset.image_url && !hasError && this.state.urlSubmit) {
      imagePreview = <img src={asset.image_url} alt="Asset preview" />;
    } else {
      imagePreview = (
        <img src={asset.image_link || asset.image_url} alt="Asset preview" />
      );
    }
    return (
      <div key={`${data.field}-input`} className="asset-image-preview slds-col">
        <p>{imageLabel}</p>
        {imagePreview}
        {deleteButton}
      </div>
    );
  }

  /**
   * Creates an Image control for the given field metadata
   *
   * @param data
   *   value: current string value
   *   field: internal name of field in redux
   *   custom: true if the field is a custom field, false otherwise
   *   label: when custom === false, id to use for translation of the field label
   *          when custom === true, the untranslatable label for the custom field
   * @returns <Input /> component
   */
  createImage(data) {
    const { formatMessage } = this.props.intl;
    const dragText = formatMessage({
      id: 'components.ecs.assetWizard.dragText'
    });
    const dropText = (
      <Button
        className="link-span"
        assistiveText={{ label: 'Upload' }}
        iconCategory="utility"
        iconName="upload"
        label={formatMessage({ id: 'components.ecs.assetWizard.uploadText' })}
        onClick={() => this.inputFileRef.current.click()}
      />
    );

    const imagePreview = this.uploadImagePreview(data);

    const previewFile = (
      <img
        className="asset-image-preview"
        style={{ display: 'none' }}
        src={this.state.url}
        alt=""
        onLoad={this.handleImageSuccess.bind(this)}
        onError={this.handleImageError.bind(this)}
      />
    );

    const chooseImageControls = (
      <div>
        <span className="upload-options">
          {formatMessage({
            id: 'components.ecs.assetWizard.uploadOptions'
          })}
        </span>
        <div className="image-buttons">
          <Button
            assistiveText={{ label: 'Add URL' }}
            label={formatMessage({
              id: 'components.ecs.assetWizard.urlOption'
            })}
            data={data}
            onClick={() => {
              this.props.actions.openModal('imageURL');
              this.setState({
                urlSubmit: false
              });
            }}
          />
          <Button
            assistiveText={{ label: 'Content Builder' }}
            label={formatMessage({
              id: 'components.ecs.assetWizard.fields.contentBuilderImageUrl'
            })}
            onClick={() => this.props.actions.openModal('contentBuilder')}
          />
        </div>
        <div className="asset-input-divider">
          <span>{formatMessage({ id: 'components.ecs.assetWizard.or' })}</span>
        </div>
        <div>
          <div
            className="asset-input-drop-box"
            onDrop={this.handleDrop.bind(this)}
            onDragOver={this.handleDragOver.bind(this)}
            onDragEnter={this.handleDragEnter.bind(this)}
            onDragLeave={this.handleDragLeave.bind(this)}
          >
            {dropText}
            <span>{dragText}</span>
          </div>
          <input
            type="file"
            id="open-file-input"
            ref={this.inputFileRef}
            style={{ display: 'none' }}
            onChange={this.handleFileSelect.bind(this)}
            onClick={event => {
              event.target.value = null;
            }}
            accept="image/x-png,image/gif,image/jpeg"
          />
        </div>
      </div>
    );

    return (
      <div className="image-url slds-col" key={`${data.field}-input`}>
        {this.state.url ? previewFile : null}
        <ImageURLModal
          actions={this.props.actions}
          data={data || {}}
          modals={this.props.modals}
          onChange={(_evt, { value }) => {
            this.props.onFieldChange(data, value, this.fields);
            if (this.props.modals.assetWizard?.data?.mode === 'create') {
              if (value.length < 1) {
                this.props.actions.updateModal('assetWizard', {
                  step0: { completed: false }
                });
              }
            }
          }}
          onSubmit={this.handleSubmit.bind(this)}
        />
        <ContentBuilderModal
          actions={this.props.actions}
          contentBuilder={this.props.contentBuilder}
          modals={this.props.modals}
          onSelect={this.handleContentBuilderSelect.bind(this)}
        />
        <div className="asset-input-container">
          {this.state.showUpload ? chooseImageControls : imagePreview}
        </div>
      </div>
    );
  }

  handlePropErrors(errors) {
    if (errors['imageSizeLimit']) {
      this.props.actions.notifyApiError({
        apiError: { code: 'IMAGE_TOO_LARGE' }
      });
    }
  }

  handleDragEnter(e) {
    e.preventDefault();
    e.stopPropagation();
    this.dragCounter++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      this.setState({ dragging: true });
    }
  }
  handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    this.dragCounter--;
    if (this.dragCounter <= 0) {
      this.setState({ dragging: false });
    }
  }
  handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
  }
  handleDrop(e) {
    const validTypes = ['image/png', 'image/gif', 'image/jpeg'];
    const isValidType = validTypes.some(vt =>
      e.dataTransfer.files[0].type.includes(vt)
    );
    if (
      e.dataTransfer.files &&
      e.dataTransfer.files.length &&
      isValidType > 0
    ) {
      getBase64(e.dataTransfer.files[0], droppedFileBase64 => {
        this.setState({
          showUpload: false,
          urlSubmit: true,
          url: droppedFileBase64
        });
        this.handleImageUpdate(droppedFileBase64);
      });
      e.dataTransfer.clearData();
      this.dragCounter = 0;
    }
    e.preventDefault();
    e.stopPropagation();
  }
  handleFileSelect(event) {
    const file =
      event.target.files && event.target.files.length && event.target.files[0];
    if (file) {
      getBase64(file, chosenFileBase64 => {
        this.setState({
          showUpload: false,
          urlSubmit: true,
          url: chosenFileBase64
        });
        this.handleImageUpdate(chosenFileBase64);
      });
    }
  }
  handleImageSuccess() {
    this.setState({
      imageLoadError: false
    });
  }
  handleImageError() {
    const { formatMessage } = this.props.intl;
    this.setState({
      imageLoadError: true
    });
    this.props.actions.updateModal('assetWizard', {
      errors: {
        image_url: formatMessage({
          id: 'components.ecs.imageURLModal.notfound'
        })
      }
    });
  }
  handleImageUpdate(url, completed = true, details = {}) {
    const { formatMessage } = this.props.intl;
    const asset = {
      ...this.props.modals.assetWizard.data.asset,
      image_url: url,
      ...details
    };

    this.props.actions.updateModal('assetWizard', {
      step0: { completed },
      asset
    });
    this.setState({
      url,
      showUpload: false
    });
    if (url && url.startsWith('data')) {
      const base64str = url.split('base64,')[1]; //remove the image type metadata.
      const size = Buffer.from(base64str, 'base64').length; //encode image into bytes
      if (size > 1024 * 1024) {
        this.props.actions.updateModal('assetWizard', {
          step0: { completed: false },
          errors: {
            image_preview: formatMessage({
              id: 'components.ecs.assetWizard.imageTooBig'
            })
          }
        });
        this.handlePropErrors({ imageSizeLimit: true });
      }
    }
  }

  // Image URL Submit
  handleSubmit(imageUrl) {
    if (imageUrl) {
      this.setState({
        urlSubmit: true,
        url: imageUrl
      });
      this.handleImageUpdate(imageUrl);
      this.props.actions.closeModal('imageURL');
    }
  }

  handleContentBuilderSelect(contentBuilderAsset) {
    const details = {
      contentBuilderAsset,
      content_builder_id: contentBuilderAsset.id,
      content_builder_type: 'image'
    };

    if (contentBuilderAsset.type === 'Image') {
      // Image types use publishedURL
      if (contentBuilderAsset.fileProperties?.publishedURL) {
        this.handleImageUpdate(
          contentBuilderAsset.fileProperties.publishedURL,
          true,
          details
        );
      }
    } else if (contentBuilderAsset.type === 'Image Block') {
      // Image Blocks have <img src="{URL}" /> tags in the contents
      const regex = new RegExp('src="(.*?)"');
      const content = contentBuilderAsset.content;
      this.handleImageUpdate(content.match(regex)[1], true, details);
    } else {
      details.content_builder_type = 'html';
      // HTML/Template blocks need to render a preview from server
      this.props.actions.fetchContentBuilderPreview(contentBuilderAsset.id);
      this.handleImageUpdate(undefined, false, details);
      this.setState({ contentBuilderPreviewAsset: contentBuilderAsset });
    }

    if (contentBuilderAsset) {
      this.props.actions.clearContentBuilderSearch();
      this.props.actions.closeModal('contentBuilder');
    }
  }

  handleInputValue(data, choices, inputValue) {
    if (inputValue.length) {
      if (
        !choices.map(c => c.id.toLowerCase()).includes(inputValue.toLowerCase())
      ) {
        if (data.multiple) {
          let values = [];
          if (Array.isArray(data.value)) {
            values = data.value;
          } else if (data.value) {
            values = [data.value];
          }
          const exists = values
            .map(value => value.toLowerCase())
            .includes(inputValue.toLowerCase());
          if (exists) return;
        }
        choices.push(this.choiceFor(inputValue, true));
      }
    }
  }

  /**
   * Creates an Combobox control for the given field metadata
   *
   * @param data
   *   value: current string value
   *   field: internal name of field in redux
   *   custom: true if the field is a custom field, false otherwise
   *   label: when custom === false, id to use for translation of the field label
   *          when custom === true, the untranslatable label for the custom field
   *   choices: list of choices for the combobox
   *   selection: selection object for selected data
   * @returns <Combobox /> component
   */
  createChoice(data) {
    const { formatMessage } = this.props.intl;
    const label = data.custom ? data.label : formatMessage({ id: data.label });
    const tooltip = data.tooltip ? (
      <Tooltip
        align="right"
        variant="learnMore"
        content={formatMessage({ id: data.tooltip })}
      />
    ) : null;
    const multiSelected = formatMessage(
      { id: 'components.ecs.assetWizard.fields.placeholder.multiSelected' },
      { count: data.selection?.length || 0 }
    );
    const choices = data.choices || [];
    const inputValue = this.state[data.field]
      ? this.state[data.field].inputValue
      : '';

    this.handleInputValue(data, choices, inputValue);

    return (
      <Combobox
        key={`${data.field}-input`}
        classNameContainer="choice-input"
        required={data.required}
        options={choices}
        selection={data.selection}
        value={inputValue}
        multiple={data.multiple}
        labels={{
          label,
          placeholder: data.placeholder,
          placeholderReadOnly: data.placeholder,
          multipleOptionsSelected: multiSelected
        }}
        fieldLevelHelpTooltip={tooltip}
        onRenderMenuItem={data.menuRenderer}
        errorText={data.errorText}
        menuItemVisibleLength={5}
        events={{
          onBlur: () => {
            if (inputValue.length) {
              let value = inputValue;
              if (data.multiple) {
                value = data.selection.map(option => option.value);
                if (!value.includes(inputValue)) {
                  value.push(inputValue);
                }
                value = value.sort((a, b) => a.localeCompare(b));
              }
              this.props.onFieldChange(data, value, this.fields);
            }
            if (this.props.onFieldBlur) {
              this.props.onFieldBlur(data, this.fields);
            }
            if (this.props.onComboboxClose) {
              this.props.onComboboxClose(data.field);
            }
            this.setState({ [data.field]: { inputValue: '' } });
          },
          onChange: (evt, { value }) => {
            this.setState({ [data.field]: { inputValue: value } });
          },
          onSelect: (evt, { selection }) => {
            let value = selection.length ? selection[0].value : null;
            if (data.tags) {
              // onSelect toggles a selected state for the given selection
              value = choices.map(choice => ({
                ...choice.tag,
                selected:
                  choice.id === selection[0].id
                    ? !choice.tag.selected
                    : choice.tag.selected
              }));
            } else if (data.multiple) {
              value = selection
                .map(option => option.value)
                .sort((a, b) => a.localeCompare(b));
            }
            this.props.onFieldChange(data, value, this.fields);
            this.setState({ [data.field]: { inputValue: '' } });
          },
          onRequestRemoveSelectedOption: (evt, { selection }) => {
            let value = null;
            if (data.tags) {
              value = choices.map(c => ({
                ...c.tag,
                selected: selection.findIndex(sel => sel.id === c.id) >= 0
              }));
            } else if (data.multiple) {
              value = selection
                .map(option => option.value)
                .sort((a, b) => a.localeCompare(b));
            }
            this.props.onFieldChange(data, value, this.fields);
          },
          onOpen: () => {
            if (this.props.onComboboxOpen) {
              this.props.onComboboxOpen(data.field);
            }
          },
          onClose: () => {
            if (this.props.onComboboxClose) {
              this.props.onComboboxClose(data.field);
            }
          }
        }}
        variant={data.variant || 'inline-listbox'}
      />
    );
  }

  /**
   * Creates a Yes/No RadioGroup control for the given field metadata
   *
   * @param data
   *   value: current value of the control.  String "Y" or "N" or null/undefined
   *   field: internal name of field in redux
   *   label: id to use for translation of the field label
   * @returns <RadioGroup /> component
   */
  createRadio(data) {
    const { formatMessage } = this.props.intl;

    return (
      <fieldset className="slds-form-element" key={`${data.field}-radio`}>
        <legend className="slds-form-element__legend slds-form-element__label">
          {formatMessage({ id: data.label })}
        </legend>
        <div className="slds-form-element__control">
          {['Y', 'N'].map(option => (
            <div key={`${data.field}-${option}-radio`}>
              <Radio
                onChange={({ target }, { checked }) => {
                  if (checked) {
                    this.props.onFieldChange(data, target.value, this.fields);
                  }
                }}
                name="availabilityRadio"
                disabled={!data.canDisableAsset && option === 'N'}
                key={`${data.field}-radio-${option}`}
                id={`${data.field}-radio-${option}`}
                labels={{
                  label: formatMessage({
                    id: `components.ecs.assetWizard.${option}`
                  })
                }}
                value={option}
                checked={data.value === option}
                variant="base"
              />
              {!data.canDisableAsset && option === 'N' ? (
                <Tooltip
                  id="edit-asset-tooltip"
                  content={formatMessage({
                    id: 'components.ecs.assetBrowser.tooltip'
                  })}
                  align="right"
                />
              ) : null}
            </div>
          ))}
        </div>
      </fieldset>
    );
  }

  /**
   * Function to disable calendar buttons for
   *  - start dates after the end date
   *  - end dates before the start date
   * @returns true if the date should be disabled, false otherwise
   */
  disableInvalidDates(dateValidation) {
    return ({ date }) =>
      (dateValidation.before && date < dateValidation.before) ||
      (dateValidation.after && date > dateValidation.after);
  }

  getLocalizedDateLabels() {
    const { formatMessage } = this.props.intl;
    return {
      placeholder: formatMessage({
        id: 'common.daterange.pick'
      }),
      abbreviatedWeekDays: [
        'sun',
        'mon',
        'tue',
        'wed',
        'thu',
        'fri',
        'sat'
      ].map(day =>
        formatMessage({
          id: `common.daterange.${day}`
        })
      ),
      months: [
        'january',
        'february',
        'march',
        'april',
        'may',
        'june',
        'july',
        'august',
        'september',
        'october',
        'november',
        'december'
      ].map(month =>
        formatMessage({
          id: `common.daterange.${month}`
        })
      ),
      today: formatMessage({
        id: 'common.daterange.today'
      })
    };
  }

  /**
   * Creates a Datepicker control for the given field metadata
   *
   * @param data
   *   value: YYYY-MM-DD string representation of the current date
   *   required: true | false if field is required
   *   field: internal name of field in redux
   *   label: id to use for translation of the field label
   *   validation:
   *      after: YYYY-MM-DD date where all dates after this date should be disabled
   *      before: YYYY-MM-DD date where all dates before this date should be disabled
   * @returns <Datepicker /> component
   */
  createDatepicker(data) {
    const { formatMessage } = this.props.intl;
    const dateLabel = formatMessage({
      id: 'components.ecs.assetWizard.fields.date'
    });
    const timeLabel = formatMessage({
      id: 'components.ecs.assetWizard.fields.time'
    });
    const timePlaceholder = formatMessage({
      id: 'components.ecs.assetWizard.fields.placeholder.time'
    });
    const dateTooltipText = !!data.tooltip ? (
      <div className="datepicker-label">
        {dateLabel}&nbsp;&nbsp;&nbsp;
        <Tooltip
          id={`${data.field}Date-tooltip`}
          align="right"
          content={formatMessage({
            id: `${data.tooltip}Date`
          })}
        />
      </div>
    ) : (
      dateLabel
    );
    const timeTooltipText = !!data.tooltip ? (
      <div className="datepicker-label">
        {timeLabel}&nbsp;&nbsp;&nbsp;
        <Tooltip
          id={`${data.field}Time-tooltip`}
          align="right"
          content={formatMessage({
            id: `${data.tooltip}Time`
          })}
        />
      </div>
    ) : (
      timeLabel
    );

    return (
      <div key={`datepicker-${data.field}-input`}>
        <h1 className="datetime-header">
          <FormattedMessage id={data.label} />
        </h1>
        <div className="datetime">
          <Datepicker
            key={`${data.field}-input`}
            className="datepicker-input"
            value={data.value?.timestamp}
            required={data.required}
            label={dateTooltipText}
            relativeYearFrom={0}
            dateDisabled={this.disableInvalidDates(data.validation)}
            formatter={date => moment(date).format('YYYY-MM-DD')}
            parser={str => moment(str, 'YYYY-MM-DD').toDate()}
            onChange={(_evt, { formattedDate }) => {
              const dateStr = formattedDate;
              const defaultTime = data.field.startsWith('start')
                ? '00:00'
                : '23:59';
              const timeStr =
                data.value?.moment?.format('HH:mm') || defaultTime;
              const dateTimeStr = `${dateStr}T${timeStr}`;
              const newMoment = moment(dateTimeStr, 'YYYY-MM-DDTHH:mm');
              const newValue = {
                ...data.value,
                dateStr,
                dateTimeStr,
                moment: newMoment,
                timestamp: newMoment.toDate()
              };
              this.props.onFieldChange(data, newValue, this.fields);
            }}
            labels={this.getLocalizedDateLabels()}
          />
          <div className="timepicker">
            <Timepicker
              key={`time-${data.field}-input`}
              required={data.required}
              label={timeTooltipText}
              stepInMinutes={30}
              onDateChange={(dateValue, stringValue) => {
                const time = moment(stringValue, 'hh:mm A');
                if (time.isValid()) {
                  const timeStr = time.format('HH:mm');
                  const dateTimeStr = `${data.value.moment.format(
                    'YYYY-MM-DD'
                  )}T${timeStr}`;
                  const newMoment = moment(dateTimeStr, 'YYYY-MM-DDTHH:mm');
                  const newValue = {
                    ...data.value,
                    timeStr: stringValue,
                    dateTimeStr,
                    moment: newMoment,
                    timestamp: newMoment.toDate()
                  };
                  this.props.onFieldChange(data, newValue, this.fields);
                }
              }}
              strValue={data.value?.timeStr || ''}
              placeholder={timePlaceholder}
              disabled={!data.value}
            />
          </div>
        </div>
      </div>
    );
  }

  timeZones() {
    return timeZoneIds.map(t => this.timeZoneFor(t));
  }

  timeZoneFor(id) {
    const { formatMessage } = this.props.intl;
    return {
      id,
      value: id,
      label: formatMessage({ id: `timeZones.${id}` })
    };
  }

  handleAssetPickerRowSelect(selection) {
    const selectedParent = selection.length ? selection[0] : null;

    this.props.actions.updateModal('assetWizard', {
      selectedParent: selectedParent
    });
    // trying to fit this into the existing pardigm... fields is neither react nor redux state...
    // ...this seems to work, but is this ok???
    this.fields = this.fields.map(function(f) {
      if (f.field === 'parent') {
        f.value = selectedParent.key;
        return f;
      } else {
        return f;
      }
    });
    this.props.onFieldChange(
      { field: 'parent' },
      selectedParent.key,
      this.fields
    );
  }

  selectedAssetType() {
    const type = this.props.asset.type;
    return this.props.assetTypes?.filter(t => t.name === type)[0];
  }

  parentAssetType() {
    const selectedAssetType = this.selectedAssetType();
    if (selectedAssetType) {
      return this.props.assetTypes.filter(
        t => t.uuid === selectedAssetType.parent_class_id
      )[0];
    }
  }

  isChildAsset() {
    return this.parentAssetType() !== undefined;
  }

  createAssetPicker(data) {
    if (!this.isChildAsset()) {
      return null;
    }
    const parentClassId = this.parentAssetType().uuid;

    const { formatMessage } = this.props.intl;
    const label = formatMessage({ id: data.label });
    const required = data.required;

    const initialParent = this.props.modals.assetWizard?.data?.asset?.parent_id;

    return (
      <React.Fragment key={`${data.field}-picker`}>
        <div className="wizard-instructions">
          {required && <span className="required">*</span>}
          {label}
        </div>
        <AssetPicker
          actions={this.props.actions}
          assets={this.props.assets}
          initialSelection={initialParent}
          onRowSelect={selection => this.handleAssetPickerRowSelect(selection)}
          context={parentClassId}
          assetType={parentClassId}
        />
      </React.Fragment>
    );
  }

  renderField(data) {
    if (Array.isArray(data)) {
      if (data.length > 0) {
        return (
          <div className="field-row" key={`${data[0].field}-row`}>
            {this.renderFields(data)}
          </div>
        );
      } else {
        return null;
      }
    } else {
      switch (data.type) {
        case 'date':
          return this.createDatepicker(data);
        case 'string':
          return this.createInput(data);
        case 'choice':
          return this.createChoice(data);
        case 'boolean':
          return this.createRadio(data);
        case 'asset':
          return this.createAssetPicker(data);
        default:
          console.warn('Unexpected data type', data.type);
          break;
      }
    }
  }
  renderFields(data) {
    return data.map(this.renderField.bind(this));
  }

  /**
   * Helper function to convert an asset object into array of custom field metadata
   *
   * NOTE: we only support string custom field types so this always returns
   * the metadata needed to generate an <Input> component (type: string)
   *
   * @param asset object representing current object ({} for create or actual asset being edited)
   * @returns array of field data for handy conversion into Input components
   */
  buildExplicitCustomFields(asset, errors) {
    const { formatMessage } = this.props.intl;
    const { attributes } = this.props;
    const choicePlaceholder = formatMessage({
      id: 'components.ecs.assetWizard.fields.placeholder.choice'
    });
    const multiChoicePlaceholder = formatMessage({
      id: 'components.ecs.assetWizard.fields.placeholder.multiChoice'
    });

    const fields = [];
    let currentRow = [];

    attributes.forEach(attr => {
      const value = (asset.custom || {})[attr.name];

      if (currentRow.length >= 2) {
        fields.push(currentRow);
        currentRow = [];
      }

      let element = {
        type: 'string',
        field: attr.name,
        label: attr.name,
        value,
        required: attr.required,
        hasError: false,
        errorText: errors && errors[attr.name] ? errors[attr.name] : null,
        custom: true
      };

      if (attr.values && attr.values.length) {
        element = {
          ...element,
          type: 'choice',
          choices: attr.values
            .map(val => ({
              id: val,
              label: val,
              value: val
            }))
            .sort((a, b) => a.label.localeCompare(b.label)),
          extraValidation: value => {
            // if user can add new values then it doesn't matter if in the list or not
            // empty values shouldn't be validated (they are validated elsewhere but only if the field is required)
            if (attr.allow_new || value === '' || value === [] || !value) {
              return true;
            }
            if (Array.isArray(value)) {
              return value.every(v => attr.values.includes(v));
            } else {
              return attr.values.includes(value);
            }
          },
          placeholder: choicePlaceholder,
          selection: value ? [this.choiceFor(value)] : [],
          variant: !attr.allow_new ? 'readonly' : 'inline-listbox'
        };
      }

      if (attr.multi_value) {
        let selection;
        if (Array.isArray(value)) {
          selection = value.map(v => this.choiceFor(v));
        } else if (value) {
          selection = [this.choiceFor(value)];
        } else {
          selection = [];
        }

        element = {
          ...element,
          type: 'choice',
          placeholder: multiChoicePlaceholder,
          multiple: true,
          selection: selection.sort((a, b) => a.label.localeCompare(b.label)),
          variant: !attr.allow_new ? 'readonly' : 'base'
        };
        if (attr.allow_new) {
          if (element.choices?.length) {
            element.choices = element.choices
              .filter(choice => {
                if (Array.isArray(value)) {
                  return !value.includes(choice.value);
                } else {
                  return value !== choice.value;
                }
              })
              .sort((a, b) => a.label.localeCompare(b.label));
          }
        } else {
          const choices = element.choices
            .map(choice => choice.label)
            .sort((a, b) => a.localeCompare(b));
          const selections = selection
            .map(choice => choice.label)
            .sort((a, b) => a.localeCompare(b));
          element.choices = [...new Set([...choices, ...selections])].map(val =>
            this.choiceFor(val)
          );
        }
      }

      currentRow.push(element);
    });

    fields.push(currentRow);
    return fields;
  }

  compareBy(field) {
    return (a, b) => {
      // Use toUpperCase() to ignore character casing
      const first = (a[field] || '').toUpperCase();
      const second = (b[field] || '').toUpperCase();

      let comparison = 0;
      if (first > second) {
        comparison = 1;
      } else if (first < second) {
        comparison = -1;
      }
      return comparison;
    };
  }

  assetTypeChoices() {
    const currentValue = this.state.type.inputValue;
    const allTypes = this.props.assetTypes || [];
    let types = allTypes.sort(this.compareBy('type'));
    if (currentValue.length) {
      types = types.filter(t =>
        t.type.toUpperCase().includes(currentValue.toUpperCase())
      );
    }
    return types.map(t => this.choiceFor(t.type, false));
  }

  choiceFor(assetType, newEntity = false) {
    const { formatMessage } = this.props.intl;
    const label = newEntity
      ? formatMessage(
          { id: 'components.ecs.assetWizard.newItem' },
          { type: assetType }
        )
      : assetType;
    return {
      id: assetType,
      label,
      value: assetType
    };
  }

  /**
   * Helper function to convert an asset object into array of standard field metadata
   *
   * @param asset object representing current object ({} for create or actual asset being edited)
   * @returns array of field data for handy conversion into Input, Datepicker, and Radio components
   */
  toStandardFields(asset, errors, canDisableAsset) {
    const timeZone = asset.display_time_zone;

    const toMoment = str => (str ? moment(str, 'YYYY-MM-DDTHH:mm') : null);

    let endDate = asset.end_datetime;
    let startDate = asset.start_datetime;

    if (typeof endDate === 'string') {
      const endMoment = toMoment(endDate);
      endDate = {
        moment: endMoment,
        dateStr: endMoment?.format('YYYY-MM-DD'),
        timeStr: endMoment?.format('hh:mm A'),
        dateTimeStr: endMoment?.format('YYYY-MM-DDTHH:mm'),
        timestamp: endMoment?.toDate(),
        timeZone
      };
    }
    if (typeof startDate === 'string') {
      const startMoment = toMoment(startDate);
      startDate = {
        moment: startMoment,
        dateStr: startMoment?.format('YYYY-MM-DD'),
        timeStr: startMoment?.format('hh:mm A'),
        dateTimeStr: startMoment?.format('YYYY-MM-DDTHH:mm'),
        timestamp: startMoment?.toDate(),
        timeZone
      };
    }

    const standardFields = [
      [
        {
          type: 'string',
          field: 'url',
          label: 'components.ecs.assetWizard.fields.url',
          tooltip: 'components.ecs.assetWizard.fields.tooltip.url',
          value: asset.url,
          required: true,
          maxLength: 2048,
          onBlur: (data, fields) => {
            if (!errors || !errors['url']) {
              this.props.onFieldChange(
                data,
                new URL(data.value).toString(),
                fields,
                false
              );
            }
          },
          extraValidation: value => {
            let regex = new RegExp('^[a-zA-Z]+://+[a-zA-Z0-9]');
            if (!regex.test(value)) {
              return false;
            }

            regex = new RegExp('%%\\[');
            if (regex.test(value)) {
              return false;
            }

            regex = new RegExp('%%=');
            if (regex.test(value)) {
              return false;
            }

            try {
              new URL(value).toString();
            } catch (ex) {
              return false;
            }

            return true;
          },
          hasError: false,
          errorText: errors && errors['url'] ? errors['url'] : null
        }
      ],
      [
        {
          type: 'string',
          field: 'subject_line',
          label: 'components.ecs.assetWizard.fields.subjectLine',
          tooltip: 'components.ecs.assetWizard.fields.tooltip.subjectLine',
          value: asset.subject_line,
          required: true,
          maxLength: 4096,
          hasError: false,
          errorText:
            errors && errors['subject_line'] ? errors['subject_line'] : null
        }
      ],
      [
        {
          type: 'string',
          field: 'preheader',
          label: 'components.ecs.assetWizard.fields.preheader',
          tooltip: 'components.ecs.assetWizard.fields.tooltip.preheader',
          value: asset.preheader,
          required: false,
          maxLength: 4096,
          hasError: false,
          errorText: errors && errors['preheader'] ? errors['preheader'] : null
        }
      ],
      [
        {
          type: 'date',
          field: 'start_datetime',
          label: 'components.ecs.assetWizard.fields.start',
          tooltip: 'components.ecs.assetWizard.fields.tooltip.start',
          value: startDate,
          validation: {
            after: endDate?.timestamp
          },
          hasError: false,
          errorText:
            errors && errors['start_datetime'] ? errors['start_datetime'] : null
        },
        {
          type: 'date',
          field: 'end_datetime',
          label: 'components.ecs.assetWizard.fields.end',
          tooltip: 'components.ecs.assetWizard.fields.tooltip.end',
          value: endDate,
          validation: {
            before: startDate?.timestamp
          },
          hasError: false,
          errorText:
            errors && errors['end_datetime'] ? errors['end_datetime'] : null
        }
      ],
      {
        type: 'choice',
        field: 'display_time_zone',
        label: 'components.ecs.assetWizard.fields.timezone',
        tooltip: 'components.ecs.assetWizard.fields.tooltip.timeZone',
        required: true,
        choices: this.timeZones(),
        value: timeZone,
        selection: timeZone ? [this.timeZoneFor(timeZone)] : [],
        hasError: false,
        errorText: errors && errors['type'] ? errors['type'] : null,
        variant: 'readonly'
      },
      [
        {
          type: 'boolean',
          field: 'availability',
          label: 'components.ecs.assetWizard.fields.availability',
          value: asset.availability,
          hasError: false,
          errorText:
            errors && errors['availability'] ? errors['availability'] : null,
          canDisableAsset
        }
      ]
    ];

    const isSubjectLineAssetClass = this.selectedAssetType()
      ?.control_subject_line;
    return isSubjectLineAssetClass
      ? standardFields
      : standardFields.filter(
          row =>
            row[0]?.field !== 'subject_line' && row[0]?.field !== 'preheader'
        );
  }

  /**
   * Helper function to convert an asset object into array of image field metadata
   *
   * @param asset object representing current object ({} for create or actual asset being edited)
   * @returns array of field data for handy conversion into Input, Datepicker, and Radio components
   */
  toImageFields(asset, errors) {
    const imageUrl = asset.image_url || '';

    return [
      [
        {
          type: 'string',
          field: 'image_url',
          label: 'components.ecs.assetWizard.fields.pasteUrl',
          tooltip: 'components.ecs.assetWizard.fields.tooltip.imageUrl',
          value: imageUrl,
          extraValidation: value => {
            this.setState({
              urlSubmit: false
            });
            let regex = new RegExp('^https?://.*$');
            if (regex.test(value)) {
              regex = new RegExp('%%\\[');
              if (regex.test(value)) {
                return false;
              }
              regex = new RegExp('%%=');
              if (regex.test(value)) {
                return false;
              }

              this.setState({
                url: value,
                imageLoadError: undefined
              });
              return true;
            } else {
              return false;
            }
          },
          hasError: false,
          errorText: errors && errors['image_url'] ? errors['image_url'] : null
        }
      ]
    ];
  }

  toClassFields(asset, errors) {
    const { formatMessage } = this.props.intl;
    const keyPlaceholder = formatMessage({
      id: 'components.ecs.assetWizard.fields.placeholder.key'
    });
    const classPlaceholder = formatMessage({
      id: 'components.ecs.assetWizard.fields.placeholder.class'
    });
    return [
      [
        {
          type: 'string',
          field: 'key',
          label: 'components.ecs.assetWizard.fields.key',
          placeholder: keyPlaceholder,
          value: asset.key,
          required: true,
          disabled: this.props.mode === 'edit',
          hasError: false,
          errorText: errors && errors['key'] ? errors['key'] : null
        }
      ],
      [
        {
          type: 'choice',
          field: 'type',
          label: 'components.ecs.assetWizard.fields.type',
          placeholder: classPlaceholder,
          value: asset.type,
          required: true,
          choices: this.assetTypeChoices(),
          selection: asset.type ? [this.choiceFor(asset.type)] : [],
          hasError: false,
          errorText: errors && errors['type'] ? errors['type'] : null
        }
      ],
      {
        type: 'asset',
        field: 'parent',
        label: 'components.ecs.assetWizard.fields.parentId',
        value: asset.parent,
        required: this.isChildAsset()
      }
    ];
  }

  formattedRange(number, type) {
    if (typeof number === 'number') {
      const { formatNumber } = this.props.intl;
      if (type === 'temperature') {
        return formatNumber(number, { maximumFractionDigits: '1' });
      } else {
        return formatNumber(number / 100, {
          style: 'percent',
          maximumFractionDigits: '1'
        });
      }
    }
  }

  weatherChoiceFor(choice, attr) {
    const { formatMessage } = this.props.intl;
    switch (attr.type) {
      case 'WeatherTemperatureAttribute':
      case 'WeatherPrecipitationAttribute':
        const ranges = attr.details?.forecast_ranges || [];
        const range = ranges.find(r => r.value === choice);

        if (!range) {
          return this.choiceFor(choice);
        } else {
          let label;
          const simpleType = attr.type
            .replace('Weather', '')
            .replace('Attribute', '')
            .toLowerCase();
          const greaterThan = this.formattedRange(
            range.greaterThan,
            simpleType
          );
          const lessThan = this.formattedRange(range.lessThan, simpleType);
          const forecastType = formatMessage({
            id: `components.ecs.assetWizard.fields.weather.${range.forecastType}`
          });
          if (greaterThan && lessThan) {
            label = formatMessage(
              {
                id: `components.ecs.assetWizard.fields.weather.${simpleType}Between`
              },
              { label: range.value, greaterThan, lessThan }
            );
          } else if (range.greaterThan) {
            label = formatMessage(
              {
                id: `components.ecs.assetWizard.fields.weather.${simpleType}GreaterThan`
              },
              { label: range.value, forecastType, greaterThan }
            );
          } else {
            label = formatMessage(
              {
                id: `components.ecs.assetWizard.fields.weather.${simpleType}LessThan`
              },
              { label: range.value, forecastType, lessThan }
            );
          }
          return {
            id: choice,
            label,
            value: choice
          };
        }
      case 'WeatherAlarmAttribute':
        const alarmLabel = formatMessage({
          id: `components.ecs.attributes.modal.weather.alarmTypes.${choice}`
        });
        return {
          id: choice,
          label: alarmLabel,
          value: choice
        };
      case 'WeatherIndexAttribute':
        const indexLabel = formatMessage({
          id: `components.ecs.attributes.modal.weather.indexCategories.${choice}`
        });
        return {
          id: choice,
          label: indexLabel,
          value: choice
        };
      default:
        return this.choiceFor(choice);
    }
  }

  toWeatherFields(asset) {
    const { formatMessage } = this.props.intl;
    const { attributes } = this.props;

    const multiChoicePlaceholder = formatMessage({
      id: 'components.ecs.assetWizard.fields.placeholder.multiChoice'
    });

    return attributes.map(attr => {
      const value = (asset.custom || {})[attr.name];
      let selection;
      if (Array.isArray(value)) {
        selection = value.map(v => this.weatherChoiceFor(v, attr));
      } else if (value) {
        selection = [this.weatherChoiceFor(value, attr)];
      } else {
        selection = [];
      }

      return [
        {
          type: 'choice',
          field: attr.name,
          label: attr.name,
          value,
          required: attr.required,
          hasError: false,
          errorText: null,
          custom: true,
          choices: attr.values.map(val => this.weatherChoiceFor(val, attr)),
          placeholder: multiChoicePlaceholder,
          multiple: true,
          selection: selection.sort((a, b) => a.label.localeCompare(b.label)),
          variant: 'readonly'
        }
      ];
    });
  }

  toTagChoice(tag, showScore) {
    const label = showScore
      ? `${tag.description} (${tag.score.toFixed(2)})`
      : tag.description;
    return {
      id: tag.mid || tag.description,
      value: tag.mid || tag.description,
      label,
      tag
    };
  }

  possibleTags(asset) {
    return (asset.ect_tags || []).map(tag => this.toTagChoice(tag));
  }

  selectedTags(asset) {
    return (asset.ect_tags || [])
      .filter(tag => tag.selected)
      .map(tag => this.toTagChoice(tag, true));
  }

  toTagFields(asset) {
    const { formatMessage } = this.props.intl;
    const selection = this.selectedTags(asset);
    const choices = this.possibleTags(asset);

    const menuRenderer = ({ option, ...props }) => {
      if (option.tag.selected) {
        return (
          <span className="tag-menu-item selected-tag-menu-item">
            <Icon
              category="utility"
              name="check"
              size="x-small"
              className="slds-icon-utility-setup"
            />
            {option.label}
          </span>
        );
      } else {
        return <span className="tag-menu-item">{option.label}</span>;
      }
    };

    const placeholder = formatMessage(
      { id: 'components.ecs.assetWizard.tagPlaceholder' },
      { tagCount: selection.length, availCount: asset.ect_tags?.length || 0 }
    );
    return [
      [
        {
          type: 'choice',
          field: 'tags',
          variant: 'base',
          label: 'components.ecs.assetWizard.fields.tags',
          value: undefined,
          menuRenderer,
          multiple: true,
          tags: true,
          placeholder,
          choices,
          selection,
          required: false,
          disabled: this.props.mode === 'edit',
          hasError: false
        }
      ]
    ];
  }

  resetImage() {
    const currentAsset = this.props.modals.assetWizard?.data?.asset || {};
    const {
      image_link,
      image_url,
      content_builder_id,
      content_builder_type,
      contentBuilderAsset,
      ect_tags,
      ...asset
    } = currentAsset;

    this.props.actions.updateModal('assetWizard', {
      step0: { completed: false },
      errors: { image_preview: null },
      asset
    });
    this.props.actions.clearTags();
    this.setState({
      showUpload: !this.state.showUpload,
      url: undefined,
      dragging: false,
      contentBuilderPreviewAsset: undefined
    });
  }

  componentDidUpdate(prevProps) {
    if (this.state.contentBuilderPreviewAsset) {
      const cbPreviewPath = `contentBuilder.data.previews.${this.state.contentBuilderPreviewAsset.id}.isLoading`;
      if (Util.propChanged(prevProps, this.props, cbPreviewPath, false)) {
        const previewId = this.state.contentBuilderPreviewAsset.id;
        const preview = this.props.contentBuilder.data.previews?.[previewId];
        const { error, dataUri } = preview || {};

        if (error) {
          this.resetImage();
          // display error
          error.fallback = { apiError: { code: 'PREVIEW_FAILED' } };
          this.props.actions.notifyApiError(error);
        } else if (dataUri) {
          // preview is done loading, if we have a dataUri, step 0 is complete
          this.setState({
            contentBuilderPreviewAsset: {
              ...this.state.contentBuilderPreviewAsset,
              dataUri
            }
          });

          const { asset } = this.props.modals.assetWizard.data;
          asset.image_url = dataUri;

          this.props.actions.updateModal('assetWizard', {
            step0: { completed: !!dataUri },
            asset
          });
        }
      }
    }
  }

  componentDidMount() {
    const { type, mode } = this.props;
    this.dragCounter = 0;
    // this forces a field change with the current value, so upstream validation can happen
    if (mode === 'edit' && ['standard', 'custom', 'weather'].includes(type)) {
      if (this.fields.length) {
        this.fields.flat().forEach(field => {
          let value = field.value;
          // in the case we are editing an asset before the default feature was implemented
          // we should choose UTC
          if (
            type === 'standard' &&
            field.field === 'display_time_zone' &&
            !field.value
          ) {
            value = 'UTC';
          }
          this.props.onFieldChange(field, value, this.fields, true);
        });
      }
    } else if (mode === 'create' && type === 'custom') {
      // on the custom page while creating a new asset, the page starts off completed = false even
      // if there are no required custom fields.
      if (this.fields.length) {
        const nonRequiredField = this.fields
          .flat()
          .find(field => !field.required);
        if (nonRequiredField) {
          this.props.onFieldChange(
            nonRequiredField,
            nonRequiredField.value,
            this.fields
          );
        }
      }
    }
  }

  imageUrl(asset) {
    if (asset?.content_builder_type === 'html') {
      const previewId = asset.contentBuilderAsset.id;
      return (
        asset.image_url ||
        this.props.contentBuilder?.data?.previews[previewId]?.dataUri
      );
    } else if (asset.image_url?.length) {
      return asset.image_url;
    } else {
      return asset.image_link;
    }
  }

  render() {
    const { type, asset, errors, canDisableAsset } = this.props;
    const { formatMessage } = this.props.intl;
    const fields = (() => {
      switch (type) {
        case 'image':
          return this.toImageFields(asset, errors);
        case 'class':
          return this.toClassFields(asset, errors);
        case 'standard':
          return this.toStandardFields(asset, errors, canDisableAsset);
        case 'weather':
          return this.toWeatherFields(asset);
        case 'tags':
          return this.toTagFields(asset);
        default:
          return this.buildExplicitCustomFields(asset, errors);
      }
    })();
    const info =
      type === 'custom' || type === 'image'
        ? formatMessage({
            id: `components.ecs.assetWizard.info.${type}`
          })
        : null;

    this.fields = fields;
    let title = formatMessage({
      id: `components.ecs.assetWizard.titles.${type}`
    });
    if (type === 'tags') {
      title = '';
    } else if (type === 'image') {
      title = (
        <div>
          <abbr className="slds-required" title="required">
            *
          </abbr>
          {title}
          <p>{info}</p>
        </div>
      );
    } else if (type === 'custom') {
      title = (
        <div>
          {title}
          <p>{info}</p>
        </div>
      );
    }

    const imageLabel = formatMessage({
      id: 'components.ecs.assetWizard.imageLabel'
    });
    const currentStep = this.props?.modals?.assetWizard?.data?.currentStep;

    const assetPreview = <img src={this.imageUrl(asset)} alt="Asset preview" />;

    const displayImagePreview =
      !!currentStep && currentStep !== 'step0' ? (
        <div className="image-preview slds-col slds-size_1-of-4">
          <p>{imageLabel}</p>
          {assetPreview}
        </div>
      ) : null;

    return (
      <div className="field-container">
        {title ? <span className="field-container-title">{title}</span> : null}
        <div className="slds-grid">
          {displayImagePreview}
          <div className="slds-col">{this.renderFields(fields)}</div>
        </div>
      </div>
    );
  }
}

AssetEditFields.propTypes = {
  actions: PropTypes.object,
  assets: PropTypes.object,
  modals: PropTypes.object,
  asset: PropTypes.object,
  errors: PropTypes.object,
  attributes: PropTypes.array,
  assetTypes: PropTypes.array,
  rules: PropTypes.object,
  type: PropTypes.oneOf([
    'image',
    'class',
    'standard',
    'custom',
    'tags',
    'weather'
  ]),
  mode: PropTypes.oneOf(['edit', 'create']),
  validation: PropTypes.array,
  onFieldBlur: PropTypes.func,
  onFieldChange: PropTypes.func.isRequired,
  onComboboxOpen: PropTypes.func,
  onComboboxClose: PropTypes.func,
  contentBuilder: PropTypes.object,
  intl: intlShape.isRequired
};

export default injectIntl(AssetEditFields);
